/**
 * Documents project management api behavior for ERP Accelerator.
 */

// Project Management API functions

/**
 * Archive a project by setting its archived flag to true.
 * Uses PUT /workflow/v2/projects/{uuid}/archive — confirmed correct from backend routes.
 */
export const archiveProject = async (id: string, API_URL: string, getAuthHeaders: () => Record<string, string>) => {
  const token = sessionStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(`${API_URL}/workflow/v2/projects/${id}/archive`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`${response.status} - ${errorText}`);
  }

  const ct = response.headers.get('content-type');
  return ct && ct.includes('application/json') ? await response.json() : { success: true };
};

/**
 * Unarchive a project by setting its archived flag to false
 */
export const unarchiveProject = async (id: string, API_URL: string, getAuthHeaders: () => Record<string, string>) => {
  const token = sessionStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(`${API_URL}/workflow/v2/projects/${id}/unarchive`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to unarchive project: ${response.status}${errorText ? ' - ' + errorText : ''}`);
  }
  return await response.json();
};

/**
 * Delete a project
 */
export const deleteProject = async (id: string, API_URL: string, getAuthHeaders: () => Record<string, string>) => {
  const token = sessionStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(`${API_URL}/workflow/v2/projects/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete project: ${response.status}${errorText ? ' - ' + errorText : ''}`);
  }
  return await response.json();
};

/**
 * Archive multiple projects by calling the single archive endpoint for each UUID.
 * The batch endpoint expects integer IDs but the frontend only has UUID strings,
 * so we loop through each UUID and call the single-project archive instead.
 */
export const batchArchiveProjects = async (projectIds: string[], API_URL: string, getAuthHeaders: () => Record<string, string>) => {
  const results = { succeeded: 0, failed: 0, errors: [] as string[] };

  for (const id of projectIds) {
    try {
      await archiveProject(id, API_URL, getAuthHeaders);
      results.succeeded++;
    } catch (error: any) {
      results.failed++;
      results.errors.push(`${id}: ${error?.message || String(error)}`);
      console.error(`[batchArchiveProjects] failed for project ${id}:`, error);
    }
  }

  if (results.failed > 0 && results.succeeded === 0) {
    throw new Error(`Failed to archive all projects. Errors: ${results.errors.join(', ')}`);
  }

  return {
    success: true,
    succeeded: results.succeeded,
    failed: results.failed,
    message: results.failed > 0
      ? `Archived ${results.succeeded} project(s), ${results.failed} failed`
      : `Successfully archived ${results.succeeded} project(s)`
  };
};

/**
 * Unarchive multiple projects in a batch operation
 */
export const batchUnarchiveProjects = async (projectIds: string[], API_URL: string, getAuthHeaders: () => Record<string, string>) => {
  const token = sessionStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(`${API_URL}/workflow/v2/projects/batch-unarchive`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ project_ids: projectIds })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to unarchive projects: ${response.status}${errorText ? ' - ' + errorText : ''}`);
  }
  return await response.json();
};

/**
 * Delete multiple projects in a batch operation
 */
export const batchDeleteProjects = async (projectIds: string[], API_URL: string, getAuthHeaders: () => Record<string, string>) => {
  const token = sessionStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  const response = await fetch(`${API_URL}/workflow/v2/projects/batch-delete`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ project_ids: projectIds })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to delete projects: ${response.status}${errorText ? ' - ' + errorText : ''}`);
  }
  return await response.json();
};

/**
 * Copy a project including all its data
 */
export const copyProject = async (id: string, API_URL: string, getAuthHeaders: () => Record<string, string>, newName?: string) => {
  const token = sessionStorage.getItem('token');
  if (!token) throw new Error('Authentication token not found');

  const body: { new_name?: string } = {};
  if (newName) body.new_name = newName;

  const response = await fetch(`${API_URL}/workflow/v2/projects/${id}/copy`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to copy project: ${response.status}${errorText ? ' - ' + errorText : ''}`);
  }
  return await response.json();
};